import React, { useEffect, useRef, useState } from 'react';
import { createStyles } from 'utils/createStyle';
import { Checkbox } from 'components/checkbox/Checkbox';
import { Btn } from 'components/btn/Btn';
import { ModalControls, useModalControls } from 'hooks/useModalControls';
import { Modal, ModalSecondaryBtn } from 'components/modal/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { colors } from 'styles/settings';
import {
    getFamilySubscriptions,
    updateFamilySubscriptions,
    groupBySubscribedAndUnSubscribed,
    normalizePreferences,
} from 'common/api/crm/family/service';
import {
    Channels,
    FamilySubscriptionResponse,
    MessageMedium,
    MessageTypes,
    Subscription,
} from 'common/api/crm/family/types';
import {
    addSubtextToSubscriptions,
    getContentfulEntriesForNotifications,
} from 'common/api/contentful/notifications/utils';

const styles = createStyles({
    header: 'text-brand-dark font-semibold text-2xl mb-10',
    subHeader: 'text-brand-dark font-semibold text-xl mb-1',
    itemHeader: 'text-grey-darkest font-semibold mb-1',
    itemdark: 'p-5 bg-grey-accent border rounded-lg border-grey-accent',
    itemlight: 'p-5',
    itemSubText: 'text-grey-medium',
    btn: ['w-full sm:w-auto mt-10 sm:mt-12 mb-5'],
    successMessage: [
        'flex gap-3 text-success rounded-lg text-sm font-normal',
        {
            background: 'rgba(56, 130, 0, 0.08)',
            padding: '12px 26px',
            width: 'fit-content',
            maxWidth: 500,
        },
    ],
    errorMessage: [
        'flex gap-3 text-error rounded-lg text-sm font-normal',
        {
            background: 'rgba(214, 0, 0, 0.08)',
            padding: '12px 26px',
            width: 'fit-content',
            maxWidth: 500,
        },
    ],
});

const Notifications = () => {
    const {
        preferences,
        updatePreferences,
        savePreferences,
        updateLastPreference,
        showSuccessMessage,
        requestMeta,
    } = useEmailPreferences();
    const modalProps = useModalControls();
    const updatePreference = (idx: number) => {
        const checked = preferences.reduce(
            (acc: number, p: Subscription) => (p.checked ? acc + 1 : acc),
            0
        );
        const isUnchecking = preferences[idx].checked;
        if (checked === 1 && isUnchecking) {
            modalProps.toggleModalVisibility();
            return;
        }
        updatePreferences(idx);
    };

    const confirm = () => {
        updateLastPreference();
        modalProps.toggleModalVisibility();
    };

    if (requestMeta.hasError) {
        return null;
    }

    return (
        <div>
            <h1 className={styles.header}>Notifications</h1>
            <div>
                <div className="mb-8">
                    <h3 className={styles.subHeader}>Email</h3>
                    <p className="text-grey-medium text-base">
                        Only receive the emails you want in your inbox. Select from the options
                        below and we’ll update your preferences.
                    </p>
                </div>
                <Preferences preferences={preferences} updatePreference={updatePreference} />
            </div>
            <Btn onClick={() => savePreferences(preferences)} className={styles.btn}>
                Save Preferences
            </Btn>
            <SuccessMessage
                show={showSuccessMessage === true}
                text="Your preferences have been saved successfully"
            />
            <ErrorMessage
                show={showSuccessMessage === false}
                text="There was an error confirming your changes. Please try again or call Customer Support at 800-727-4715 (option 3) for assistance."
            />
            <UncheckWarningModal {...modalProps} confirm={confirm} />
        </div>
    );
};

export const Preferences = ({
    preferences,
    updatePreference,
}: {
    preferences: Subscription[];
    updatePreference: (_: number) => void;
}) => (
    <ul>
        {preferences.map((preference: Subscription, idx: number) => (
            <li className={idx % 2 > 0 ? styles.itemlight : styles.itemdark} key={idx}>
                <div className="flex items-start gap-4">
                    <Checkbox
                        text=""
                        checked={preference.checked}
                        onChange={() => {
                            updatePreference(idx);
                        }}
                    />
                    <div className="text-base">
                        <p className={styles.itemHeader}>{preference.title}</p>
                        <p className={styles.itemSubText}>{preference.subText}</p>
                    </div>
                </div>
            </li>
        ))}
    </ul>
);

type Message = {
    show: boolean;
    text: string;
};

const SuccessMessage = ({ show, text }: Message) => {
    if (!show) {
        return null;
    }

    return (
        <div className={styles.successMessage}>
            <span>
                <FontAwesomeIcon icon={faCheck} />
            </span>
            <p>{text}</p>
        </div>
    );
};

const ErrorMessage = ({ show, text }: Message) => {
    if (!show) {
        return null;
    }

    return (
        <div className={styles.errorMessage}>
            <span>
                <FontAwesomeIcon icon={faExclamationTriangle} />
            </span>
            <p>{text}</p>
        </div>
    );
};

type VoidFn<T = void> = (d: T) => void;
type EmailPreferencesHook = {
    preferences: Subscription[];
    updatePreferences: VoidFn<number>;
    savePreferences: VoidFn<Subscription[]>;
    updateLastPreference: VoidFn;
    showSuccessMessage: boolean | null;
    requestMeta: RequestMeta;
};

type RequestMeta = {
    hasError: boolean;
    loading: boolean | null;
};

const useEmailPreferences = (): EmailPreferencesHook => {
    const originalPreferences = useRef<Subscription[]>([]);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean | null>(null);
    const [preferences, setPreferences] = useState<Subscription[]>([]);
    const [request, setRequest] = useState<RequestMeta>({ hasError: false, loading: null });
    const pushSubscriptions = useRef<MessageMedium[]>([]);

    const updatePreferences = (index: number) => {
        setPreferences((preferences: Subscription[]) => {
            const p = [...preferences];
            p[index] = { ...p[index], checked: !p[index].checked };
            return p;
        });
    };

    async function savePreferences(prefs: Subscription[]) {
        const updatedPreferences = mergeEmailPreferences(pushSubscriptions.current, prefs);
        const payload = groupBySubscribedAndUnSubscribed(updatedPreferences);

        const result = await updateFamilySubscriptions(payload);

        if (!result.error) {
            setPreferences(prefs);
            setShowSuccessMessage(true);
            originalPreferences.current = prefs; // override the old original value to the updated set.
        } else {
            // revert to the previous changes in case the request failed
            setShowSuccessMessage(false);
            setPreferences(originalPreferences.current);
        }
    }

    const updateLastPreference = async () => {
        const i = preferences.findIndex((p) => p.checked);

        const newPrefs = [...preferences];
        newPrefs[i] = { ...newPrefs[i], checked: !newPrefs[i].checked };
        await savePreferences(newPrefs);
    };

    useEffect(() => {
        (async () => {
            setRequest((req) => ({ ...req, loading: true }));
            const result = await getFamilySubscriptions();
            if (result.data) {
                const prefs = normalizePreferences(result.data);
                const res = transformToEmailSubscriptions(result.data);
                pushSubscriptions.current = prefs;
                if (res) {
                    const data = res;
                    const prefs = addSubtextToSubscriptions(
                        data,
                        await getContentfulEntriesForNotifications()
                    );
                    setPreferences(prefs);
                    originalPreferences.current = prefs;
                }
            }
            setRequest({ loading: false, hasError: !result });
        })();
    }, []);

    return {
        preferences,
        updatePreferences,
        savePreferences,
        updateLastPreference,
        showSuccessMessage,
        requestMeta: request,
    };
};

const mergeEmailPreferences = (mediums: MessageMedium[], emailPrefs: Subscription[]) => {
    for (let i = 0; i < mediums.length; ++i) {
        const medium = mediums[i];

        for (let j = 0; j < medium.channels.length; ++j) {
            const channel = medium.channels[j];
            if (channel.channelName === 'Marketing Channel') {
                channel.messageTypes = emailPrefs.map((k) => {
                    return {
                        messageTypeId: k.id,
                        messageTypeName: k.title,
                        isSubscribed: k.checked,
                    };
                });
            }
        }
    }
    return mediums;
};

const transformToEmailSubscriptions = (
    data: FamilySubscriptionResponse | undefined | null
): Subscription[] | undefined => {
    if (data) {
        const emailSubs = data.messageMediums.find(
            (medium: MessageMedium) => medium.messageMedium === 'Email'
        );
        const emailChannels = emailSubs?.channels.find(
            (channel: Channels) => channel.channelName === 'Marketing Channel'
        );
        const subscriptions = emailChannels?.messageTypes.map((messageType: MessageTypes) => {
            return {
                id: messageType.messageTypeId,
                title: messageType.messageTypeName,
                subText: '',
                checked: messageType.isSubscribed && emailChannels.isSubscribed,
            };
        });

        return subscriptions || [];
    }
};

const UncheckWarningModal = ({
    isVisible,
    toggleModalVisibility,
    confirm,
}: ModalControls & { confirm: () => void }) => {
    return (
        <Modal title="" isOpen={isVisible} onClose={toggleModalVisibility}>
            <FontAwesomeIcon
                color={colors.warningDark}
                icon={faExclamationTriangle}
                className="text-4xl mx-auto"
            />
            <h1 className="text-brand-dark text-2xl sm:text-29px font-semibold mt-4 sm:mt-6 mx-auto">
                Are you sure?
            </h1>
            <p className="text-grey-medium text-center mt-2.5 sm:mt-4 text-17px">
                You will be unsubscribed from all RaiseRight marketing emails. If you are an active
                participant, you will still receive transactional emails such as account and order
                related updates.
            </p>
            <div className="flex flex-col gap-7 w-full sm:w-40 mx-auto mt-7">
                <Btn onClick={confirm}>Confirm</Btn>
                <ModalSecondaryBtn onClick={toggleModalVisibility}>Cancel</ModalSecondaryBtn>
            </div>
        </Modal>
    );
};

export default Notifications;
